/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import PasswordValidator from 'password-validator';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {formatRequirements} from './PasswordValidationHelpers';

export const defaultConfiguration = {
  passwordMinLength         : '12',
  passwordMaxLength         : '64',
  passwordRequireUpper      : true,
  passwordRequireLower      : true,
  passwordRequireDigit      : true,
  passwordRequireSymbol     : true,
  passwordDisallowSpaces    : true,
  passwordDisallowUsername  : true,
  passwordDisallowGroupName : false,
  passwordIsPhrase          : false
};

/**
 * Provides the single point of entry into the Password Policy API, which can be
 * configured specifically via the configuration argument, or default to being
 * configured via the password policy defined in the environment.json document.
 *
 * The API includes a validate method which, when called with a password, returns
 * a message containing any rule failures, or undefined if validation passes. The
 * isValid method returns true if the given password is valid, and false if not.
 * The requirements property provides a description of all password rules based
 * on the given configurations (for display to the user).
 *
 * ```typescript
 *   import passwordPolicy from 'routes/Admin/Users/state/passwordPolicy';
 *   import {parseExtendedAttributes} from 'core/services/Connect';
 *
 *   // create a password policy based on the provided username and group name,
 *   // using the default environment.json password configuration ...
 *   const {isValid, validate, requirements} = passwordPolicy.passwordPolicy(
 *     user.username,
 *     user.account.title
 *   );
 *
 *   // create a password policy based on the provided username and group name,
 *   // overriding the default configuration with an account specific password
 *   // policy ...
 *   const policy = parseExtendedAttributes(account, true).passwordPolicy;
 *   const {isValid, validate, requirements} = passwordPolicy.passwordPolicy(
 *     user.username,
 *     user.account.title,
 *     policy
 *   );
 *
 *   // determine if a password is valid ...
 *   isValid('somePassword123');
 *
 *   // determine if a password is valid, and if not, have a failure message
 *   // returned which can be displayed to the user to inform them why the
 *   // password has failed ...
 *   validate('somePassword123');
 *
 *   // display the formatted password policy requirements ...
 *   <Tooltip title={requirements}>
 *     <InfoIcon />
 *   </Tooltip>
 *
 * ```
 *
 * @method  passwordPolicy
 * @param   {string} username The username of the user for which the password is
 *          to be validated. When the password policy's passwordDisallowUsername
 *          rule is set to true, the password will be validated to ensure that
 *          the username is not contained within it.
 * @param   {string} groupName The name of the group to which the user belongs.
 *          When the password policy's passwordDisallowGroupName rule is set to
 *          true, the password will be validated to ensure that the  groupName
 *          is not contained within it.
 * @param   {object} configuration An optional configuration rule-set. By default,
 *          if a configuration is not provided, the API falls back to using the
 *          password policy defined within environment.json.
 * @returns {object} An object containing a requirements property, and a validate
 *          method. The requirements property provides the validation message to
 *          be  displayed to the user, and the validate method performs password
 *          validation, returning a validation error string which contains the
 *          rules which have failed (if any), otherwise, returns undefined if the
 *          password is valid.
 */
export const passwordPolicy = (
  username: string,
  groupName: string,
  configuration: GenericObject = {}
): GenericObject => {
  const configurations = configuration || defaultConfiguration;
  const {policy, requirements} = createPolicy(username, groupName, configurations);
  return {
    validate : password => validate(password, policy, {includeFailures: true, configurations}),
    isValid  : password => validate(password, policy, {includeFailures: false, configurations}),
    requirements
  };
};

/**
 * Determines and returns a formatted string which outlines the Password Validation
 * Rules based on the provided configurations and / or default configuration as
 * specified via environment.json.
 *
 * ```typescript
 *   import * as passwordPolicy 'routes/Admin/Users/state/passwordPolicy';
 *
 *   // display the formatted password policy requirements ...
 *   <Tooltip title={passwordPolicy.getRequirements()}>
 *     <InfoIcon />
 *   </Tooltip>
 *
 * ```
 *
 *
 * @method getRequirements
 * @param  {object} configuration An optional configuration rule-set. By default, if
 *         a configuration is not provided, the API falls back to using the password
 *         policy defined within the environment.json configuration.
 * @param  {string} configuration A user friendly formatted string which outlines the
 *         Password Validation requirements.
 */
export const getRequirements = (config?: GenericObject): string => {
  const rules = getConfiguration(config);
  const path = 'common:passwordPolicy';
  const includes = [];
  const excludes = [];
  rules.passwordRequireUpper && includes.push(translate(`${path}.uppercase`));
  rules.passwordRequireLower && includes.push(translate(`${path}.lowercase`));
  rules.passwordRequireDigit && includes.push(translate(`${path}.digits`));
  rules.passwordRequireSymbol && includes.push(translate(`${path}.symbols`));
  rules.passwordDisallowSpaces && excludes.push(translate(`${path}.spaces`));
  rules.passwordDisallowUsername && excludes.push(translate(`${path}.username`));
  rules.passwordDisallowGroupName && excludes.push(translate(`${path}.groupName`));
  return translate(`${path}.requirements`, {
    min      : rules.passwordMinLength,
    max      : rules.passwordMaxLength,
    includes : formatRequirements(includes, true),
    excludes : formatRequirements(excludes, false)
  });
};

// /**
//  * @private
//  *
//  * Resolves the current password policy configuration based on the provided
//  * config, falling back to the default configuration provided by environment
//  * json if not specified.
//  *
//  * @method  getConfiguration
//  * @param {object} configuration An optional password policy configuration
//  *          to be used.
//  * @returns {object} The password policy configuration as specified via the
//  *          config argument and default configuration provided via environment
//  *          json.
//  */
export const getConfiguration = (config?: GenericObject): GenericObject => {
  const fallback = getDefaultConfiguration();
  return config ? Object.assign({}, fallback, config) : fallback;
};

// /**
//  * @private
//  *
//  * Performs validation on the provided password, returning a validation
//  * failure message containing all failed rules, otherwise, returns
//  * undefined if all rules have been met (password is valid).
//  *
//  * @method  validate
//  * @param   {string} password The password that is to be validated.
//  * @param   {PasswordValidator} policy The password policy against which
//  *          the password is to be validated.
//  * @param   {boolean} includeFailures When true (default), if the validation
//  *          fails the rules which have not been met will be returned, i.e.
//  *          a validation error message will be returned. Otherwise, when false,
//  *          returns true if the validation passes, and false if it fails.
//  * @returns {string|undefiend} If valid, returns undefined, otherwise, if
//  *          invalid, returns a validation failure message which indicates
//  *          the rules which have not been met.
//  */
export const validate = (
  password: string,
  policy,
  parameters: {includeFailures: boolean, configurations: GenericObject}
): string | undefined => {
  const {includeFailures, configurations} = parameters;
  if (includeFailures) {
    const validation = policy.validate(password, {list: true});
    return constructMessage(validation, configurations);
  }
  return policy.validate(password);
};

// /**
//  * Creates a Password Policy instance and requirements message based on the
//  * given password policy rules specified via environment.json.
//  *
//  * @private
//  * @method  createPolicy
//  * @param   {string} username The username of the user for which the password is
//  *          to be validated. When the password policy's passwordDisallowUsername
//  *          rule is set to true, the passoword will be validated to ensure that
//  *          the username is not contained within it.
//  * @param   {string} groupName The name of the group to which the user belongs.
//  *          When the password policy's passwordDisallowGroupName rule is set to
//  *          true, the passoword will be validated to ensure that the  groupName
//  *          is not contained within it.
//  * @param   {object} config An optional configuration rule-set. By default, if
//  *          a configuration is not provided, the API falls back to using the
//  *          password policy defined within the environment.json configuration.
//  * @returns {object} An object containing a policy, and requirements property.
//  *          The policy property is a PasswordValidator instance which is to be
//  *          used for executing validation. The requirements property provides
//  *          the validation message to be displayed to the user. Both properties
//  *          are constructed based on the password policy values defined within
//  *          the environment.json document.
//  */
export const createPolicy = (username: string, groupName: string, config: GenericObject): GenericObject => {
  const policy = new PasswordValidator();
  const rules = getConfiguration(config);
  const blacklist = [];
  policy.is().min(rules.passwordMinLength);
  policy.is().max(rules.passwordMaxLength);
  rules.passwordRequireUpper && policy.has().uppercase();
  rules.passwordRequireLower && policy.has().lowercase();
  rules.passwordRequireDigit && policy.has().digits();
  rules.passwordRequireSymbol && policy.has().symbols();
  rules.passwordDisallowSpaces && policy.has().not().spaces();
  rules.passwordDisallowUsername && username && blacklist.push(username);
  rules.passwordDisallowGroupName && groupName && blacklist.push(groupName);
  policy.not().oneOf(blacklist);
  return {policy, requirements: getRequirements(config)};
};

// /**
//  *
//  * @private
//  *
//  * Provides a utility which, given a list of Password Validator failures,
//  * and an optional configuration, returns a user friendly message to be
//  * displayed.
//  *
//  * @method  constructMessage
//  * @param   {array} failures An array of Password Validator failures.
//  * @returns {string} A user-friendly message containing the reasons why
//  *          the password is invalid.
//  */
export const constructMessage = (failures: string[], config: GenericObject = {}): string => {
  const {
    passwordMinLength,
    passwordMaxLength,
    passwordDisallowUsername,
    passwordDisallowGroupName,
    passwordIsPhrase
  } = getConfiguration(config);
  const path = 'common:passwordPolicy';
  const failed = mapFailures(failures);
  const includes = [];
  const excludes = [];
  let errors = '';
  errors = failed.min ? translate(`${path}.min`, {min: passwordMinLength}) : errors;
  errors = failed.max ? translate(`${path}.max`, {max: passwordMaxLength}) : errors;
  failed.uppercase && includes.push(translate(`${path}.uppercase`));
  failed.lowercase && includes.push(translate(`${path}.lowercase`));
  failed.digits && includes.push(translate(`${path}.digits`));
  failed.symbols && includes.push(translate(`${path}.symbols`));
  failed.spaces && excludes.push(translate(`${path}.spaces`));
  failed.oneOf && passwordDisallowUsername && excludes.push(translate(`${path}.username`));
  failed.oneOf && passwordDisallowGroupName && excludes.push(translate(`${path}.groupName`));
  errors = formatErrors(errors, includes, {path, includes: true, passwordIsPhrase});
  errors = formatErrors(errors, excludes, {path, includes: false, passwordIsPhrase});
  return translate(`${path}.errors`, {errors});
};

// /**
//  * @private
//  * @param {string} message, a string of error messages.
//  * @param {string[]} items, an array of validation rules, ex. "Digit (0-9)".
//  * @param {string} path, path to validation rules
//  * Provides a utility which formats validation error messages.
//  */
function formatErrors(message: string, items: string[], extraInputs: GenericObject): string {
  if (items?.length) {
    const parameters = extraInputs.includes ? 'includes' : 'excludes';
    const conjunction = translate('common:conjunctions.and');
    const inclusions = translate(`${extraInputs.path}.${parameters}`, {
      [parameters] : formatRequirements(items, true)
    });
    return extraInputs?.passwordIsPhrase?.isPhrase
      ? message ? `${message} ${inclusions}` : inclusions
      : message ? `${message}, ${conjunction} ${inclusions}` : inclusions;
  }
  return message;
};

// /**
//  * @private
//  * Provides a simple mapping of Password Validator failures to an object
//  * of keys for each failure.
//  * @param {string[]} an array of strings listing the validation rules that failed.
//  */
const mapFailures = (failures: string[]): GenericObject => {
  const failed = {};
  failures.forEach(failure => {
    failed[failure] = true;
  });
  return failed;
};

// /**
//  * Returns the default password policy configuration defined in environment.json.
//  *
//  * @private
//  * @method  getDefaultConfiguration
//  * @returns {object} An object containing the password requirement rules defined
//  *          via environment.json.
//  */
export const getDefaultConfiguration = (): GenericObject => {
  return defaultConfiguration;
};
export default passwordPolicy;
